// ═══════════════════════════════════════════════════════════════
// PA Grants — Applicant Portal Layout
// Separate layout for pa_applicant_* roles with scoped navigation
// ═══════════════════════════════════════════════════════════════

import { useEffect, useRef } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { Group, Button, Center, Loader } from '../../../components/gab/GabComponents';
import {
  IconLayoutDashboard, IconClipboardList, IconAlertTriangle,
  IconFileDescription, IconFileSpreadsheet, IconReceipt, IconClock,
} from '@tabler/icons-react';
import { useMyPAOrg } from '../../../hooks/useMyPAOrg';
import { usePAGrantsStore } from '../../../store/paGrantsStore';
import { useRoleStore } from '../../../store/roleStore';
import PortalDashboard from './PortalDashboard';
import PortalProjects from './PortalProjects';
import PortalDamageSites from './PortalDamageSites';
import PortalDocuments from './PortalDocuments';
import PortalOnboarding from './PortalOnboarding';

const PORTAL_NAV = [
  { label: 'Dashboard', path: '/pagrants/pa', icon: IconLayoutDashboard, exact: true },
  { label: 'My Projects', path: '/pagrants/pa/projects', icon: IconClipboardList },
  { label: 'Damage Sites', path: '/pagrants/pa/damage', icon: IconAlertTriangle },
  { label: 'Documents', path: '/pagrants/pa/documents', icon: IconFileDescription },
  { label: 'Reimbursements', path: '/pagrants/pa/reimbursements', icon: IconReceipt },
  { label: 'Time Extensions', path: '/pagrants/pa/time-extensions', icon: IconClock },
  { label: 'FEMA Forms', path: '/pagrants/pa/forms', icon: IconFileSpreadsheet, mgrOnly: true },
];

function resolvePortalPage(pathname: string) {
  if (pathname === '/pagrants/pa' || pathname === '/pagrants/pa/') return <PortalDashboard />;
  // Project list vs. project detail — detail falls through to router Outlet (ProjectWorkspace)
  if (pathname === '/pagrants/pa/projects') return <PortalProjects />;
  if (pathname.startsWith('/pagrants/pa/projects/')) return null;
  if (pathname.startsWith('/pagrants/pa/damage')) return <PortalDamageSites />;
  if (pathname.startsWith('/pagrants/pa/documents')) return <PortalDocuments />;
  if (pathname.startsWith('/pagrants/pa/reimbursements')) return null; // Falls through to router
  if (pathname.startsWith('/pagrants/pa/time-extensions')) return null; // Falls through to router
  if (pathname.startsWith('/pagrants/pa/forms')) return null;
  // Any other path (e.g., /pagrants/pa/applicants/:orgId/projects/:id) falls through to Outlet
  if (pathname.startsWith('/pagrants/pa/applicants')) return null;
  return <PortalDashboard />;
}

export default function ApplicantPortalLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const myOrg = useMyPAOrg();
  const organizations = usePAGrantsStore(s => s.organizations);
  const loadingOrgs = usePAGrantsStore(s => s.loadingOrgs);
  const fetchAll = usePAGrantsStore(s => s.fetchAll);
  const effectiveRole = useRoleStore(s => s.demoRoleOverride ?? s.myRole);
  const isMgr = effectiveRole?.roleSlug === 'pa_applicant_mgr';
  const fetched = useRef(false);

  useEffect(() => {
    if (!fetched.current) {
      fetched.current = true;
      fetchAll();
    }
  }, [fetchAll]);

  // Only show loader on initial load (no data yet)
  if (loadingOrgs && organizations.length === 0) {
    return (
      <Center p="xl" style={{ minHeight: 300 }}>
        <Loader size="sm" />
      </Center>
    );
  }

  if (!myOrg && !loadingOrgs) {
    return <PortalOnboarding />;
  }

  const visibleNav = PORTAL_NAV.filter(item => !item.mgrOnly || isMgr);

  // For FEMA Forms, let the router's Outlet handle it
  const portalPage = resolvePortalPage(pathname);

  return (
    <div style={{ padding: '24px' }}>
      <Group gap="xs" mb="md" style={{ borderBottom: '1px solid var(--frost)', paddingBottom: 12, flexWrap: 'wrap' }}>
        {visibleNav.map(item => {
          const active = item.exact ? pathname === item.path : pathname.startsWith(item.path);
          return (
            <Button
              key={item.path}
              variant={active ? 'filled' : 'light'}
              color={active ? '#1E40AF' : 'gray'}
              size="xs"
              leftSection={<item.icon size={14} />}
              onClick={() => navigate(item.path)}
            >
              {item.label}
            </Button>
          );
        })}
      </Group>
      {portalPage || <Outlet />}
    </div>
  );
}
